import { useId, useRef, useState } from 'react'
import { api } from '../lib/api.ts'
import { StatusBadge } from './StatusBadge.tsx'
import { StatusTimeline, type StatusStop } from './StatusTimeline.tsx'

/**
 * The status badge, and where it has been on a click.
 *
 * The histórico modal carries the timeline, but "how did it get here" is
 * asked from the header, where the status is, and a modal is a lot of door
 * for one line of dots. So the badge opens a popover with the same timeline
 * beneath it, read once on first open. Light-dismiss: a click anywhere else
 * closes it, as a person expects of something that popped out of a badge.
 */
export function StatusPopover({
  verificationId,
  status,
}: {
  verificationId: string
  status: string
}) {
  const id = useId()
  const anchor = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const [statuses, setStates] = useState<readonly StatusStop[] | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [at, setAt] = useState<{ left: number; top: number; width: number } | undefined>()

  const open = () => {
    const trigger = anchor.current
    const popover = panel.current
    if (trigger === null || popover === null) return
    if (popover.matches(':popover-open')) {
      popover.hidePopover()
      return
    }
    const box = trigger.getBoundingClientRect()
    const width = Math.min(560, window.innerWidth - 16)
    setAt({
      left: Math.max(8, Math.min(box.left, window.innerWidth - width - 8)),
      top: box.bottom + 6,
      width,
    })
    popover.showPopover()
    if (statuses === undefined && error === undefined) {
      void api
        .conferenceHistory(verificationId)
        .then((answer) => setStates(answer.statuses ?? []))
        .catch((cause: unknown) =>
          setError(cause instanceof Error ? cause.message : 'falha ao ler o histórico'),
        )
    }
  }

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="inline-flex cursor-pointer rounded-full border-0 bg-transparent p-0"
        aria-haspopup="dialog"
        aria-controls={id}
        onClick={open}
      >
        <StatusBadge status={status} />
      </button>
      <div
        ref={panel}
        id={id}
        role="dialog"
        aria-label="Linha do tempo da conferência"
        popover="auto"
        className="bg-background-raised border-stroke-weak m-0 rounded-[8px] border p-4 shadow-lg"
        style={{
          position: 'fixed',
          left: at?.left ?? 0,
          top: at?.top ?? 0,
          width: at?.width ?? 0,
          inset: 'auto',
        }}
        onToggle={(event) => {
          if ((event as unknown as { newStatus: string }).newStatus === 'closed') setAt(undefined)
        }}
      >
        {error !== undefined && <p className="text-tP text-text-error">{error}</p>}
        {statuses === undefined && error === undefined && (
          <p className="text-tP text-text-weak">Carregando…</p>
        )}
        {statuses !== undefined && statuses.length === 0 && (
          <p className="text-tP text-text-weak">Sem estados registrados.</p>
        )}
        {statuses !== undefined && statuses.length > 0 && <StatusTimeline statuses={statuses} />}
      </div>
    </>
  )
}
